import { Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Lock, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

/**
 * Page-level gate. Wrap premium-only pages with this component.
 * Free users see an upgrade screen; premium users + admins pass through.
 */
export default function PremiumGate({ feature, children }) {
  const { user } = useAuth();
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  if (isPremium) return children;

  return (
    <div className="max-w-2xl mx-auto fade-up" data-testid="premium-gate">
      <div className="rounded-3xl bg-white border border-slate-200 p-8 sm:p-10 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[var(--cavilo-blue)]/10 blur-2xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-amber-200/30 blur-2xl pointer-events-none" />
        <div className="relative">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-amber-100 text-amber-600">
            <Lock size={26} />
          </span>
          <div className="chip chip-amber mt-5" style={{ width: "fit-content" }}>
            <Sparkles size={12} /> Premium feature
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-3 text-[var(--cavilo-ink)]">
            {feature || "This feature"} is part of Cavilow Premium.
          </h1>
          <p className="mt-3 text-slate-600 leading-relaxed max-w-lg">
            Your free plan includes resume upload, the full ATS review, and the tutorial.
            Unlock everything else — improved resumes, tailoring, cover letters, interview practice,
            job search and the application tracker — with Cavilow Premium.
          </p>

          <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
            {[
              "Generate improved + multi-version resumes",
              "Tailor your resume to any job description",
              "Cover letter writer with 5 tones",
              "Interview practice (40+ questions per role)",
              "Find jobs across global platforms",
              "Kanban-style application tracker",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2">
                <ShieldCheck size={15} className="text-emerald-600 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/pricing"
              data-testid="premium-gate-upgrade"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-6 py-3.5 text-sm font-bold shadow-lg shadow-blue-500/25 btn-press"
            >
              <Sparkles size={16} /> Take the premium plan
              <ArrowRight size={14} />
            </Link>
            <Link
              to="/upload"
              data-testid="premium-gate-upload"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white border-2 border-slate-200 hover:border-slate-300 text-[var(--cavilo-ink)] px-6 py-3.5 text-sm font-bold"
            >
              Upload resume (free)
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Inline upgrade ad — drop into pages that work for free but want to surface premium.
 * Visible only to non-premium users.
 */
export function UpgradeAd({ feature = "everything else", testId = "upgrade-ad", compact = false }) {
  const { user } = useAuth();
  const isPremium = user?.subscription === "premium" || user?.is_admin;
  if (isPremium) return null;

  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-3xl ink-card text-white p-6 sm:p-8 fade-up"
    >
      <div className="absolute -right-6 -top-6 w-44 h-44 rounded-full bg-[var(--cavilo-blue)]/30 blur-2xl pointer-events-none" />
      <div className="absolute -left-6 -bottom-6 w-44 h-44 rounded-full bg-amber-300/20 blur-2xl pointer-events-none" />
      <div className="relative grid sm:grid-cols-[1fr_auto] items-center gap-5">
        <div>
          <div className="chip chip-amber" style={{ width: "fit-content" }}>
            <Sparkles size={12} /> Unlock more
          </div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-3">
            Want to do more with your resume?
          </h3>
          <p className="text-sm text-slate-300 mt-2 max-w-xl leading-relaxed">
            Your ATS score is just the start. Generate improved & tailored resumes,
            create cover letters, practice interviews, and find matching jobs — all in one place.
          </p>
          {!compact && (
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-slate-200">
              {[
                "Improved & tailored resumes",
                "Cover letter writer",
                "Interview practice (40+ Qs)",
                "Find jobs globally",
                "Application tracker",
                "Unlimited generations",
              ].map((f) => (
                <li key={f} className="flex items-center gap-1.5">
                  <Sparkles size={11} className="text-amber-300 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Link
          to="/pricing"
          data-testid={`${testId}-cta`}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-[var(--cavilo-ink)] px-6 py-3.5 text-sm font-bold hover:bg-slate-100 btn-press shrink-0"
        >
          <Sparkles size={14} /> Take the premium plan <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
